import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Flower } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { VerificationBadge } from "@/components/MemorialBadges";

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

const EMPTY = { name: "", dob: "", dop: "", class_batch: "", graduation_year: "", institution: "", profession: "", epitaph: "", bio: "" };

export default function GroupMemorials({ groupId, isMember }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [photoId, setPhotoId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/groups/${groupId}/memorials`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [groupId]);

  const onPhoto = async (e) => { const file = e.target.files?.[0]; if (file) { try { setPhotoId(await uploadPhoto(file)); toast.success("Photo attached"); } catch { toast.error("Upload failed"); } } };
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post(`/groups/${groupId}/memorials`, { ...f, photo_file_id: photoId });
      setOpen(false); setF(EMPTY); setPhotoId(null); load();
      toast.success("Memorial added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Remember members of this community who have passed away.</p>
        {isMember && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="rounded-full gap-2" data-testid="add-memorial-btn"><Plus size={15} /> Add a memorial</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Add a memorial</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Full name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="memorial-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Born (e.g. 1972)" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} />
                  <Input placeholder="Passed (e.g. 2021)" value={f.dop} onChange={(e) => setF({ ...f, dop: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Class / batch" value={f.class_batch} onChange={(e) => setF({ ...f, class_batch: e.target.value })} />
                  <Input placeholder="Graduation year" value={f.graduation_year} onChange={(e) => setF({ ...f, graduation_year: e.target.value })} />
                </div>
                <Input placeholder="Institution" value={f.institution} onChange={(e) => setF({ ...f, institution: e.target.value })} />
                <Input placeholder="Profession / contribution" value={f.profession} onChange={(e) => setF({ ...f, profession: e.target.value })} />
                <Input placeholder="Epitaph / short quote" value={f.epitaph} onChange={(e) => setF({ ...f, epitaph: e.target.value })} />
                <Textarea placeholder="A short biography (optional)" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} />
                <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                  <Plus size={15} /> {photoId ? "Photo attached" : "Add a photo"} <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                </label>
              </div>
              <DialogFooter><Button onClick={submit} disabled={busy} className="rounded-full" data-testid="memorial-save">{busy ? "Saving…" : "Add memorial"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/60 p-10 text-center text-sm text-muted-foreground">
          <Flower size={36} weight="duotone" className="mx-auto mb-2 text-secondary" /> No memorials yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((m) => {
            const years = [m.dob, m.dop].filter(Boolean).join(" – ");
            return (
              <Link key={m.id} to={`/memorials/${m.id}`} data-testid={`group-memorial-${m.id}`} className="flex flex-col items-center rounded-2xl border border-border/60 bg-card p-5 text-center transition-colors hover:border-secondary/50">
                <div className="mb-3 h-20 w-20 overflow-hidden rounded-full ring-4 ring-secondary/20">
                  {m.photo_file_id ? <img src={fileUrl(m.photo_file_id)} alt={m.name} className="h-full w-full object-cover" /> : <span className="grid h-full w-full place-items-center bg-accent text-xl font-heading text-primary">{(m.name || "?")[0]}</span>}
                </div>
                <p className="font-heading font-medium">{m.name}</p>
                {years && <p className="text-xs text-muted-foreground">{years}</p>}
                {m.epitaph && <p className="mt-1 text-xs italic text-foreground/70">“{m.epitaph}”</p>}
                {m.verification_status && <div className="mt-2"><VerificationBadge status={m.verification_status} /></div>}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
